import { useEffect } from "react";
import type { DocHandle } from "../sync/docManager";
import { docManager } from "../sync/docManager";
import { useDocSelector } from "../core/useDocSelector";
import { readCurrent, reconcileOpenEntries, shardIdFor, stopTracking } from "./timelog";
import { readTask } from "../core/tasks";

/**
 * Keeps the current month's shard open and applies the reconciliation rule (spec §9.1) whenever
 * `tracking.current` changes: any open entry that is no longer current gets closed deterministically.
 */
export function useTrackingReconcile(core: DocHandle | null, workspaceId: string | null): void {
  const current = useDocSelector(core, (doc) => readCurrent(doc));
  // finishing the task that is being tracked ends its timer — from any device, the API or an agent
  const finished = useDocSelector(core, (doc) => {
    const cur = readCurrent(doc);
    const task = cur ? readTask(doc, cur.taskId) : undefined;
    if (!cur || !task) return false;
    if (task.doneAt) return true;
    const last = task.props["rec.lastDoneAt"];
    return typeof last === "string" && Date.parse(last) >= Date.parse(cur.startedAt);
  });
  useEffect(() => {
    if (finished && core && !core.readOnly) void stopTracking(core.doc);
  }, [finished, core]);
  const key = current ? `${current.entryId}:${current.docId}` : "none";
  useEffect(() => {
    if (!core || !workspaceId || core.readOnly) return;
    let alive = true;
    const ids = new Set<string>([shardIdFor(workspaceId, new Date())]);
    if (current?.docId) ids.add(current.docId);
    void (async () => {
      for (const id of ids) {
        const shard = await docManager.open(id);
        if (!alive) return;
        // wait for the server's view before closing entries other devices opened
        if (shard.settled) reconcileOpenEntries(core.doc, shard);
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [core?.docId, workspaceId, key]);
}
